import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import {
  MdAssignment,
  MdCheckCircle,
  MdWarning,
  MdCancel,
  MdAutorenew,
  MdPayments,
  MdTrendingUp,
  MdArrowForward,
} from "react-icons/md";

// TODO:
// import {
//   getAMCDashboard,
//   getTodayAMCDashboard,
// } from "../../../redux/services/amcDashboardServices/amcDashboard.Services";

const AMCDashboard = () => {
  const [view, setView] = useState("overall");
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({});
  const [todaySummary, setTodaySummary] = useState({});

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);

        // const [overall, today] = await Promise.all([
        //   getAMCDashboard(),
        //   getTodayAMCDashboard(),
        // ]);

        // setSummary(overall?.data || {});
        // setTodaySummary(today?.data || {});

        setSummary({});
        setTodaySummary({});
      } catch (error) {
        console.error("AMC Dashboard Error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const overallTiles = useMemo(
    () => [
      {
        icon: <MdAssignment />,
        label: "Total Contracts",
        href: "/amc-contract-list",
        points: summary?.total_contracts || 0,
        accent: "cyan",
        helper: "All AMC contracts",
      },
      {
        icon: <MdCheckCircle />,
        label: "Active Contracts",
        href: "/amc-contract-list",
        points: summary?.active_contracts || 0,
        accent: "green",
        helper: "Currently active",
      },
      {
        icon: <MdWarning />,
        label: "Expiring Soon",
        href: "/amc-expiring-list",
        points: summary?.expiring_soon || 0,
        accent: "orange",
        helper: "Upcoming expiry",
      },
      {
        icon: <MdCancel />,
        label: "Expired",
        href: "/amc-expired-list",
        points: summary?.expired || 0,
        accent: "red",
        helper: "Expired contracts",
      },
      {
        icon: <MdAutorenew />,
        label: "Renewals",
        href: "/amc-renewal-list",
        points: summary?.renewals || 0,
        accent: "purple",
        helper: "Renewal contracts",
      },
      {
        icon: <MdPayments />,
        label: "Renewal Revenue",
        href: "/amc-revenue",
        points: `₹${summary?.renewal_revenue || 0}`,
        accent: "blue",
        helper: "Renewal revenue",
      },
    ],
    [summary]
  );

  const todayTiles = useMemo(
    () => [
      {
        icon: <MdAssignment />,
        label: "New Contracts",
        href: "/amc-contract-list",
        points: todaySummary?.new_contracts || 0,
        accent: "cyan",
        helper: "Today's contracts",
      },
      {
        icon: <MdAutorenew />,
        label: "Renewals Today",
        href: "/amc-renewal-list",
        points: todaySummary?.renewals || 0,
        accent: "purple",
        helper: "Today's renewals",
      },
      {
        icon: <MdWarning />,
        label: "Expiring Soon",
        href: "/amc-expiring-list",
        points: todaySummary?.expiring_soon || 0,
        accent: "orange",
        helper: "Upcoming expiry",
      },
      {
        icon: <MdPayments />,
        label: "Revenue Today",
        href: "/amc-revenue",
        points: `₹${todaySummary?.revenue || 0}`,
        accent: "green",
        helper: "Today's revenue",
      },
      {
        icon: <MdCancel />,
        label: "Expired Today",
        href: "/amc-expired-list",
        points: todaySummary?.expired || 0,
        accent: "red",
        helper: "Expired contracts",
      },
    ],
    [todaySummary]
  );

  const currentData = view === "overall" ? summary : todaySummary;
  const tiles = view === "overall" ? overallTiles : todayTiles;

  const contractChart = {
    series: [
      Number(currentData?.active_contracts || 0),
      Number(currentData?.expiring_soon || 0),
      Number(currentData?.expired || 0),
    ],
    options: {
      chart: {
        type: "donut",
        toolbar: { show: false },
      },
      labels: ["Active", "Expiring Soon", "Expired"],
      legend: {
        position: "bottom",
      },
    },
  };

  const renewalPerformance = currentData?.renewal_performance || [];

  const renewalChart = {
    series: [
      {
        name: "Renewals",
        data: renewalPerformance.map((item) =>
          Number(item?.renewals || 0)
        ),
      },
      {
        name: "Revenue",
        data: renewalPerformance.map((item) =>
          Number(item?.revenue || 0)
        ),
      },
    ],
    options: {
      chart: {
        type: "bar",
        toolbar: { show: false },
      },
      xaxis: {
        categories: renewalPerformance.map(
          (item) => item?.month || item?.date || ""
        ),
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          columnWidth: "45%",
        },
      },
      legend: {
        position: "bottom",
      },
    },
  };

  if (loading) {
    return (
      <div className="crm-dashboard crm-dashboard-loading">
        <div className="crm-loader-ring"></div>
      </div>
    );
  }

  return (
    <div className="crm-dashboard">
      <section className="crm-dashboard-hero">
        <div className="crm-hero-glow"></div>

        <div className="crm-hero-content">
          <div>
            <span className="crm-ai-badge">
              <MdAssignment /> AMC
            </span>

            <h1>AMC Management Dashboard</h1>

            <p>
              Monitor contracts, expiry schedules, renewals and AMC revenue
              from one place.
            </p>

            <div className="crm-hero-meta">
              <span>
                <MdTrendingUp />
                Contract management analytics
              </span>
            </div>
          </div>

          <div className="crm-hero-stats">
            <div>
              <strong>{currentData?.active_contracts || 0}</strong>
              <span>Active Contracts</span>
            </div>

            <div>
              <strong>{currentData?.expiring_soon || 0}</strong>
              <span>Expiring Soon</span>
            </div>
          </div>
        </div>
      </section>

      <div className="crm-section-head">
        <div>
          <span className="crm-eyebrow">AMC OVERVIEW</span>
          <h2>Contract Analytics</h2>
        </div>

        <div className="crm-view-switch">
          <button
            className={view === "overall" ? "active" : ""}
            onClick={() => setView("overall")}
          >
            Overall
          </button>

          <button
            className={view === "today" ? "active" : ""}
            onClick={() => setView("today")}
          >
            Today
          </button>
        </div>
      </div>

      <div className="crm-kpi-grid">
        {tiles.map((item, index) => (
          <Link
            to={item.href}
            className={`crm-kpi-card ${item.accent}`}
            key={index}
          >
            <div className="crm-kpi-top">
              <div className="crm-kpi-icon">{item.icon}</div>
              <MdArrowForward className="crm-kpi-arrow" />
            </div>

            <div className="crm-kpi-value">{item.points}</div>
            <div className="crm-kpi-label">{item.label}</div>
            <div className="crm-kpi-helper">{item.helper}</div>
            <div className="crm-kpi-line"></div>
          </Link>
        ))}
      </div>

      <div className="crm-chart-grid">
        <div className="crm-chart-card">
          <div className="crm-card-heading">
            <div>
              <span className="crm-live-dot"></span>
              Contract Status
            </div>
            <span className="crm-period-chip">Live</span>
          </div>

          <div className="crm-chart-body">
            <Chart
              options={contractChart.options}
              series={contractChart.series}
              type="donut"
              height={320}
            />
          </div>
        </div>

        <div className="crm-chart-card">
          <div className="crm-card-heading">
            <div>
              <span className="crm-live-dot"></span>
              Renewal Performance
            </div>
            <span className="crm-period-chip">Monthly</span>
          </div>

          <div className="crm-chart-body">
            {renewalPerformance.length ? (
              <Chart
                options={renewalChart.options}
                series={renewalChart.series}
                type="bar"
                height={320}
              />
            ) : (
              <div className="crm-empty-chart">
                No renewal data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AMCDashboard;